// src/components/home/packFinder.js
//
// Pure logic behind the "Find your pack" wizard (home-packfinder). No DOM, no React,
// no network: the wizard is a lookup table Danielle edits in Site Text, and this file
// is the only place that interprets it. Deterministic by construction -- there is no
// AI here and nothing to hallucinate.
//
// Every string the visitor reads comes from Site Text (defaults in
// src/config/siteText.js DEFAULT_PACK_FINDER). Nothing about packs, papers or prices
// is decided in this file; it only matches answers to the outcomes it is given.
import { CIE_NOTE } from './marketingCopy.js';
import { DEFAULT_PACK_FINDER } from '../../config/siteText.js';

const str = (v) => (typeof v === 'string' ? v : '');
const norm = (v) => str(v).trim().toLowerCase();
const pick = (v, fallback) => (str(v).trim() || fallback);
const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);

// The editable UI strings. Each falls back to its shipped default when blank, so a
// cleared field can never produce an unlabelled button.
export const PACK_FINDER_LABEL_KEYS = [
    'heading', 'subheading', 'ctaLabel', 'restartLabel', 'backLabel', 'resultLabel',
    'tryFreeLabel', 'seeInsideLabel', 'libraryLabel', 'seeAllLabel',
];

/* ------------------------------------------------------------------ cleaning ---- */

// Questions the wizard can actually ask: a label, a usable id, and at least one option
// with both a value and a label. Anything else is dropped rather than rendered broken
// (an unanswerable question would strand the visitor with no result).
//   - a blank id gets a positional one ('q1', 'q2' ...), so the question still works;
//   - a DUPLICATE id is dropped (two questions writing one answer would silently
//     overwrite each other);
//   - duplicate option values within a question keep the first.
export function cleanQuestions(list) {
    const out = [];
    const seen = new Set();
    (Array.isArray(list) ? list : []).forEach((q, i) => {
        if (!isObj(q)) return;
        const label = str(q.label).trim();
        if (!label) return;
        const id = norm(q.id) || ('q' + (i + 1));
        if (seen.has(id)) return;
        const values = new Set();
        const options = (Array.isArray(q.options) ? q.options : [])
            .filter(isObj)
            .map((o) => ({ value: norm(o.value), label: str(o.label).trim() }))
            .filter((o) => {
                if (!o.value || !o.label || values.has(o.value)) return false;
                values.add(o.value);
                return true;
            });
        if (!options.length) return;
        seen.add(id);
        out.push({ id, label, options });
    });
    return out;
}

// The filled-in conditions of an outcome, as [questionId, value] pairs. A BLANK value
// means "any answer" and is not a condition -- that is what lets every outcome carry
// the same three keys in the admin editor (which can fill a field but cannot add one).
export function conditionsOf(outcome) {
    const when = isObj(outcome) && isObj(outcome.when) ? outcome.when : {};
    return Object.keys(when)
        .map((k) => [norm(k), norm(when[k])])
        .filter(([k, v]) => k && v);
}

export const isCatchAll = (outcome) => conditionsOf(outcome).length === 0;

// Outcomes worth keeping: an object that has something to SAY (a title or a why).
// packIds are trimmed and de-duplicated; they are resolved against the live catalogue
// later (resolvePacks), so an unknown id is harmless here.
export function cleanOutcomes(list) {
    return (Array.isArray(list) ? list : [])
        .filter(isObj)
        .map((o) => ({
            when: Object.fromEntries(conditionsOf(o)),
            packIds: [...new Set((Array.isArray(o.packIds) ? o.packIds : []).map((id) => str(id).trim()).filter(Boolean))],
            title: str(o.title).trim(),
            why: str(o.why).trim(),
        }))
        .filter((o) => o.title || o.why);
}

/* ----------------------------------------------------------------- resolving ---- */

// Site Text -> the config the component renders.
//
// WHY THE FALLBACKS: on the live site state.siteText is the SAVED doc, not a merge with
// the defaults (useSiteContentBridge replaces it). Until Danielle next saves Site Text,
// `packFinder` is simply absent there -- so an absent block means "the shipped wizard",
// not "no wizard". Once she has saved, her block is authoritative:
//   - questions / outcomes: HER list if the key holds an array, even an empty one
//     (emptying it is a way to switch the wizard off); the defaults only when the key
//     is missing altogether;
//   - enabled: only an explicit `false` hides it.
//
// `fallback` is the shipped catch-all. It is what the visitor sees if her outcomes
// somehow have no catch-all and nothing matches -- the result is never blank.
export function resolvePackFinder(siteText) {
    const st = isObj(siteText) ? siteText : {};
    const raw = isObj(st.packFinder) ? st.packFinder : {};
    const labels = {};
    for (const k of PACK_FINDER_LABEL_KEYS) labels[k] = pick(raw[k], DEFAULT_PACK_FINDER[k]);
    const questions = cleanQuestions(Array.isArray(raw.questions) ? raw.questions : DEFAULT_PACK_FINDER.questions);
    const outcomes = cleanOutcomes(Array.isArray(raw.outcomes) ? raw.outcomes : DEFAULT_PACK_FINDER.outcomes);
    // Add a non-purchasable CIE path only to the recognised original board menu.
    // Existing custom CIE menus/outcomes stay under Danielle's control.
    const board = questions.find(q => q.id === 'board');
    if (outcomes.length && board && board.options.length === 3 && ['edexcel', 'oxfordaqa', 'unsure'].every(v => board.options.some(o => o.value === v))) {
        board.options.splice(2, 0, { value: 'cie', label: 'Cambridge IGCSE (CIE) — coming soon' });
        if (!outcomes.some(o => o.when.board === 'cie')) outcomes.unshift({ when: { board: 'cie' }, packIds: [], title: CIE_NOTE, why: 'Cambridge content is in preparation. The current Edexcel and OxfordAQA packs follow their own specifications. Please check back for Cambridge pack details before choosing.' });
    }
    const fallback = cleanOutcomes(DEFAULT_PACK_FINDER.outcomes).find(isCatchAll) || null;
    const enabled = raw.enabled !== false;
    return {
        ...labels, enabled, questions, outcomes, fallback,
        // The component's one render gate, kept here so it is tested rather than inlined.
        visible: enabled && questions.length > 0 && outcomes.length > 0,
    };
}

// answers ({ questionId: optionValue }) -> the outcome to show.
//
//   1. An outcome MATCHES when every filled-in condition equals the visitor's answer.
//   2. Among the matches, the one with the MOST conditions wins; a tie goes to the one
//      listed FIRST. Authors still order specific -> general, and for a list ordered
//      that way this is exactly "first match wins".
//   3. If no conditional outcome matches, the catch-all (every condition blank) is used.
//   4. No catch-all either -> null, and the caller shows the shipped fallback.
//
// WHY "most specific" AND NOT LITERALLY "first": the admin editor can only APPEND a
// new row, so anything Danielle adds lands at the bottom -- after the catch-all and
// after the general outcomes. Under a strict first-match rule that new row could never
// win, and re-mapping the wizard would need a developer. A catch-all likewise only
// ever loses to a real match, wherever it sits in the list.
export function resolveOutcome(answers, outcomes) {
    const given = {};
    if (isObj(answers)) for (const k of Object.keys(answers)) given[norm(k)] = norm(answers[k]);
    let best = null;
    let bestCount = 0;
    let catchAll = null;
    for (const o of (Array.isArray(outcomes) ? outcomes : [])) {
        if (!isObj(o)) continue;
        const conds = conditionsOf(o);
        if (!conds.length) { if (!catchAll) catchAll = o; continue; }
        if (!conds.every(([k, v]) => given[k] === v)) continue;
        if (conds.length > bestCount) { best = o; bestCount = conds.length; }
    }
    return best || catchAll || null;
}

// An outcome's packIds -> the live pack objects, in the outcome's order. An id that is
// not in the catalogue (retired, unpublished, mistyped) is SKIPPED SILENTLY: the
// visitor still gets the outcome's words, never an error or an empty card.
export function resolvePacks(packIds, packs) {
    const list = Array.isArray(packs) ? packs : [];
    const out = [];
    const seen = new Set();
    for (const raw of (Array.isArray(packIds) ? packIds : [])) {
        const id = str(raw).trim();
        if (!id || seen.has(id)) continue;
        const pack = list.find((p) => isObj(p) && p.id === id);
        if (!pack) continue;
        seen.add(id);
        out.push(pack);
    }
    return out;
}

// The primary button under a recommended pack: where it goes and which of the three
// labels it carries. The SAME honesty rule as the home page's free-lesson CTA
// (proofStrip.js freeLessonOffer): "free lesson" is a claim, so it is only made when a
// free lesson is really there.
//
//   signed in                          -> their library
//   coming soon                        -> no button (the card already says so)
//   the pack has free items            -> that pack's public preview
//   a BUNDLE whose member has free items -> the preview of the member with the MOST
//                                          free items. A bundle has no
//                                          lessons of its own; the member's lesson IS
//                                          part of what the bundle unlocks, so the
//                                          claim is true.
//   otherwise                          -> "see what's inside", routed exactly as the
//                                          card's own View button routes a signed-out
//                                          visitor with no free items (/login).
export function packAction(pack, packs, signedIn) {
    if (!isObj(pack)) return null;
    if (signedIn) return { kind: 'library', href: '/dashboard' };
    if (pack.status !== 'available') return null;
    if (Number(pack.freeItems) > 0) return { kind: 'free', href: '/preview/' + pack.id };
    const list = Array.isArray(packs) ? packs : [];
    // The member with the MOST free items (first wins a tie): the fullest sample is the
    // fairest picture of what the bundle unlocks.
    const member = (Array.isArray(pack.memberPackIds) ? pack.memberPackIds : [])
        .map((id) => list.find((p) => isObj(p) && p.id === id))
        .filter((p) => p && p.status === 'available' && Number(p.freeItems) > 0)
        .reduce((best, p) => (!best || Number(p.freeItems) > Number(best.freeItems) ? p : best), null);
    if (member) return { kind: 'free', href: '/preview/' + member.id };
    return { kind: 'inside', href: '/login' };
}

/* ------------------------------------------------------------------- linting ---- */

// Every answer combination the questions allow (capped -- three short questions is 63).
export function answerCombos(questions, cap = 5000) {
    let combos = [{}];
    for (const q of (Array.isArray(questions) ? questions : [])) {
        const next = [];
        for (const c of combos) for (const o of q.options) {
            next.push({ ...c, [q.id]: o.value });
            if (next.length > cap) return next;
        }
        combos = next;
    }
    return combos;
}

// Authoring checks, in plain words. The wizard never throws on bad data (it degrades),
// so a mistake in Site Text is otherwise silent -- a typo'd option value just means an
// outcome that never appears. This names those mistakes. Pass the live packs to also
// check pack ids; pass [] (or nothing) to skip that check.
export function lintPackFinder(config, packs) {
    const cfg = isObj(config) ? config : {};
    const questions = Array.isArray(cfg.questions) ? cfg.questions : [];
    const outcomes = Array.isArray(cfg.outcomes) ? cfg.outcomes : [];
    const warnings = [];
    const byId = new Map(questions.map((q) => [q.id, new Set(q.options.map((o) => o.value))]));
    const name = (o, i) => '#' + (i + 1) + (o.title ? ' "' + o.title + '"' : '');

    if (!outcomes.some(isCatchAll)) {
        warnings.push('There is no catch-all outcome (one with every "when" field blank). Add one so an unusual combination of answers still gets a helpful result.');
    }
    outcomes.forEach((o, i) => {
        for (const [k, v] of conditionsOf(o)) {
            if (!byId.has(k)) warnings.push('Outcome ' + name(o, i) + ' depends on "' + k + '", which is not the id of any question.');
            else if (!byId.get(k).has(v)) warnings.push('Outcome ' + name(o, i) + ' needs ' + k + ' to be "' + v + '", but no option of that question has that value.');
        }
    });
    const known = new Set((Array.isArray(packs) ? packs : []).filter(isObj).map((p) => p.id));
    if (known.size) {
        outcomes.forEach((o, i) => {
            for (const id of (o.packIds || [])) {
                if (!known.has(id)) warnings.push('Outcome ' + name(o, i) + ' recommends "' + id + '", which is not a pack in the live catalogue (it will be skipped).');
            }
        });
    }
    if (outcomes.filter(isCatchAll).length > 1) {
        warnings.push('There is more than one catch-all outcome; only the first one listed is ever used.');
    }
    // Reachability: an outcome no combination of answers can ever land on. (A catch-all
    // that nothing falls through to is a safety net doing its job, not a mistake.)
    const reached = new Set(answerCombos(questions).map((a) => resolveOutcome(a, outcomes)));
    outcomes.forEach((o, i) => {
        if (!reached.has(o) && !isCatchAll(o)) warnings.push('Outcome ' + name(o, i) + ' can never be shown: every combination of answers that matches it is won by a more specific outcome, or by an earlier one with the same number of conditions.');
    });
    return warnings;
}
